import React, { useState, useEffect } from 'react';
import { Link, NavLink as RouterNavLink, useLocation } from 'react-router-dom';
import styled from '@emotion/styled';

const Header = styled.header`
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  padding: 1rem;
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const Nav = styled.nav`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Logo = styled(Link)`
  color: white;
  text-decoration: none;
  font-size: 1.25rem;
  font-weight: bold;
  display: flex;
  align-items: center;
  
  span {
    color: #ff3333;
  }
`;

const MaintenanceOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 3000;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
`;

const MaintenanceCard = styled.div`
  width: 100%;
  max-width: 820px;
  background: rgba(0, 0, 0, 0.55);
  border: 2px solid rgba(255, 51, 51, 0.35);
  border-radius: 16px;
  padding: 2.25rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.92);

  @media (max-width: 768px) {
    padding: 1.75rem;
  }
`;

const MaintenanceTitle = styled.h1`
  margin: 0 0 0.75rem;
  font-size: 2rem;
  font-weight: 700;
  color: white;

  span {
    color: #ff3333;
  }
`;

const MaintenanceText = styled.p`
  margin: 0.75rem 0 0;
  font-size: 1.1rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.88);
`;

const SupportEmailLink = styled.a`
  color: white;
  text-decoration: none;

  &:hover {
    color: #ff3333;
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const NavLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: center;
  
  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    flex-direction: column;
    width: 100%;
    padding: 1rem 0;
    gap: 1rem;
  }
`;

const NavLink = styled(RouterNavLink)`
  color: white;
  text-decoration: none;
  font-size: 1rem;
  transition: color 0.2s;
  padding: 0.5rem;
  border-bottom: 2px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;

  span {
    color: #ff3333;
  }

  &.active {
    color: #ff3333;
    border-bottom-color: rgba(255, 51, 51, 0.7);
  }
  
  &:hover {
    color: #ff3333;
  }
`;

const ClientPortal = styled(Link)`
  background: #ff3333;
  color: white;
  text-decoration: none;
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-size: 0.9rem;
  transition: background 0.2s;
  
  &:hover {
    background: #ff4444;
  }
`;

const LayoutWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Main = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Footer = styled.footer`
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(10px);
  padding: 1rem;
  text-align: center;
  font-size: 0.9rem;
  margin-top: auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const FooterLink = styled(Link)`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  font-size: 0.9rem;
  
  &:hover {
    color: white;
  }
`;

const Copyright = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  text-align: center;
  margin-top: 1rem;
  
  span {
    color: #ff3333;
  }
`;

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const isMaintenanceModeEnabled =
    String(process.env.REACT_APP_MAINTENANCE_MODE || '').toLowerCase() === 'true';

  // Close menu when location changes (i.e., when a link is clicked)
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  return (
    <LayoutWrapper>
      {isMaintenanceModeEnabled && (
        <MaintenanceOverlay>
          <MaintenanceCard>
            <MaintenanceTitle>
              Red<span>Cloud</span> Storage
            </MaintenanceTitle>
            <MaintenanceText>
              Our platform is currently undergoing maintenance. Please check back again later.
            </MaintenanceText>
            <MaintenanceText>
              For urgent requests, contact us at{' '}
              <SupportEmailLink href="mailto:support@redcloudstorage.net">support@redcloudstorage.net</SupportEmailLink>.
            </MaintenanceText>
          </MaintenanceCard>
        </MaintenanceOverlay>
      )}
      <Header>
        <Nav>
          <Logo to="/">Red<span>Cloud</span> &nbsp;Storage</Logo>
          <MenuButton onClick={toggleMenu} aria-label="Toggle navigation menu">
            {isMenuOpen ? '✕' : '☰'}
          </MenuButton>
          <NavLinks isOpen={isMenuOpen}>
            <NavLink to="/about">About</NavLink>
            <NavLink to="/why-redcloud">Why Red<span>Cloud</span></NavLink>
            <NavLink to="/features">Features</NavLink>
            <NavLink to="/pricing">Pricing</NavLink>
            <NavLink to="/ownership-transfer">Transfer</NavLink>
            <NavLink to="/download">Download</NavLink>
            <NavLink to="/how-to-use">How To</NavLink>
            <NavLink to="/contact">Contact</NavLink>
            <ClientPortal to="https://my.redcloudstorage.net">Client Portal →</ClientPortal>
          </NavLinks>
        </Nav>
      </Header>

      <Main>{children}</Main>

      <Footer>
        <FooterContent>
          <FooterLinks>
            <FooterLink to="/privacy">Privacy Policy</FooterLink>
            <FooterLink to="/terms">Terms of Service</FooterLink>
            <FooterLink to="/legal">Legal Notice</FooterLink>
          </FooterLinks>
          <Copyright>
            <Link to="/copyright" style={{ color: 'rgba(255, 255, 255, 0.7)', textDecoration: 'none' }}>
              &copy; {new Date().getFullYear()} Red<span>Cloud</span> Storage. All rights reserved.
            </Link>
          </Copyright>
        </FooterContent>
      </Footer>
    </LayoutWrapper>
  );
};

export default Layout;
